import { Layout } from "@/components/layout/layout";
import { Link, useLocation } from "wouter";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  CardFooter,
} from "@/components/ui/card";
import { useState } from "react";
import { useFieldErrors } from "@workspace/api-client-react";
import { usePlayerAuthActions } from "@/hooks/useCurrentPlayer";
import { useToast } from "@/hooks/use-toast";
import { Loader2 } from "lucide-react";

export default function Signup() {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [password, setPassword] = useState("");
  const [, setLocation] = useLocation();
  const { toast } = useToast();
  const { signup } = usePlayerAuthActions();
  const formErrors = useFieldErrors();

  const handleSignup = (e: React.FormEvent) => {
    e.preventDefault();
    formErrors.clear();
    if (!name || !email || !password) return;
    if (password.length < 8) {
      toast({
        title: "Password too short",
        description: "Pick a password with at least 8 characters.",
        variant: "destructive",
      });
      return;
    }

    signup.mutate(
      {
        data: {
          name: name.trim(),
          email: email.trim(),
          phone: phone.trim() || null,
          password,
        },
      },
      {
        onSuccess: () => {
          toast({
            title: "Welcome to the Circuit",
            description: "Your player profile has been created.",
          });
          setLocation("/me");
        },
        onError: (err: unknown) => {
          const envelope = formErrors.setFromError(err);
          const message = envelope.message ?? "Could not create profile.";
          toast({ title: "Sign up failed", description: message, variant: "destructive" });
        },
      },
    );
  };

  const fieldErrorOf = (name: string) => formErrors.fieldError(name);
  const renderFieldError = (name: string) => {
    const err = fieldErrorOf(name);
    return err ? (
      <p className="text-xs text-destructive mt-1" data-testid={`error-${name}`}>
        {err}
      </p>
    ) : null;
  };

  return (
    <Layout>
      <div className="flex-1 flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8 bg-background relative">
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,_var(--tw-gradient-stops))] from-primary/5 via-background to-background" />

        <Card className="w-full max-w-md relative z-10 border-border/50 shadow-2xl shadow-primary/5">
          <CardHeader className="space-y-1 text-center pb-8 pt-8">
            <CardTitle className="text-3xl font-bold tracking-tight">Join the Circuit</CardTitle>
            <CardDescription className="text-base text-muted-foreground pt-2">
              Create your player profile to track stats and register for tournaments.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSignup} className="space-y-5" noValidate>
              {formErrors.formError &&
              Object.keys(formErrors.fieldErrors).length === 0 ? (
                <p
                  className="text-sm text-destructive"
                  data-testid="error-form"
                >
                  {formErrors.formError}
                </p>
              ) : null}
              <div className="space-y-2">
                <Label htmlFor="name" className="text-foreground font-medium">
                  Full Name
                </Label>
                <Input
                  id="name"
                  placeholder="John Smith"
                  value={name}
                  onChange={(e) => {
                    setName(e.target.value);
                    formErrors.clearField("name");
                  }}
                  required
                  aria-invalid={fieldErrorOf("name") ? true : undefined}
                  className={`h-12 bg-muted/50 focus:bg-background ${fieldErrorOf("name") ? "border-destructive" : ""}`}
                />
                {renderFieldError("name")}
              </div>
              <div className="space-y-2">
                <Label htmlFor="email" className="text-foreground font-medium">
                  Email Address
                </Label>
                <Input
                  id="email"
                  type="email"
                  placeholder="name@example.com"
                  value={email}
                  onChange={(e) => {
                    setEmail(e.target.value);
                    formErrors.clearField("email");
                  }}
                  required
                  autoComplete="email"
                  aria-invalid={fieldErrorOf("email") ? true : undefined}
                  className={`h-12 bg-muted/50 focus:bg-background ${fieldErrorOf("email") ? "border-destructive" : ""}`}
                />
                {renderFieldError("email")}
              </div>
              <div className="space-y-2">
                <Label htmlFor="phone" className="text-foreground font-medium">
                  Phone Number{" "}
                  <span className="text-muted-foreground font-normal">(Optional)</span>
                </Label>
                <Input
                  id="phone"
                  type="tel"
                  placeholder="+61 400 000 000"
                  value={phone}
                  onChange={(e) => {
                    setPhone(e.target.value);
                    formErrors.clearField("phone");
                  }}
                  aria-invalid={fieldErrorOf("phone") ? true : undefined}
                  className={`h-12 bg-muted/50 focus:bg-background ${fieldErrorOf("phone") ? "border-destructive" : ""}`}
                />
                {renderFieldError("phone") ?? (
                  <p className="text-xs text-muted-foreground mt-1">
                    Used for tournament updates and waitlists.
                  </p>
                )}
              </div>
              <div className="space-y-2">
                <Label htmlFor="password" className="text-foreground font-medium">
                  Password
                </Label>
                <Input
                  id="password"
                  type="password"
                  placeholder="At least 8 characters"
                  value={password}
                  onChange={(e) => {
                    setPassword(e.target.value);
                    formErrors.clearField("password");
                  }}
                  required
                  minLength={8}
                  autoComplete="new-password"
                  aria-invalid={fieldErrorOf("password") ? true : undefined}
                  className={`h-12 bg-muted/50 focus:bg-background ${fieldErrorOf("password") ? "border-destructive" : ""}`}
                />
                {renderFieldError("password")}
              </div>
              <Button
                type="submit"
                className="w-full h-12 text-base font-semibold mt-6"
                disabled={signup.isPending || !name || !email || !password}
              >
                {signup.isPending ? <Loader2 className="mr-2 h-5 w-5 animate-spin" /> : null}
                {signup.isPending ? "Creating Profile..." : "Create Profile"}
              </Button>
            </form>
          </CardContent>
          <CardFooter className="flex flex-col items-center justify-center border-t border-border/50 p-6 bg-muted/10">
            <p className="text-sm text-muted-foreground">
              Already a regular?{" "}
              <Link href="/login" className="text-primary font-semibold hover:underline">
                Sign In
              </Link>
            </p>
          </CardFooter>
        </Card>
      </div>
    </Layout>
  );
}
